import { useState } from "react";
import { Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileArrowUp, faSpinner, faCheck, faForward, faXmark, faCheckSquare } from "@fortawesome/free-solid-svg-icons";
import crudApi from "@serie3/common/api/crudApi";
import { btn, btnHoverStyle } from "../../../layout/Buttons/buttonStyles";

const MAPPING_FIELDS = [
  { key: "betreff",            label: "Subject" },
  { key: "beschreibung",       label: "Description" },
  { key: "datum",              label: "Date" },
  { key: "brutto",             label: "Gross" },
  { key: "netto",              label: "Net" },
  { key: "finalBillingNumber", label: "Invoice Nr" },
  { key: "paymentdate",        label: "Paid On" },
  { key: "bezahlt",            label: "Paid" },
  { key: "status",             label: "Status" },
  { key: "currency",           label: "Currency" },
  { key: "typ",                label: "Type" },
  { key: "kundeName",          label: "Company" },
  { key: "personVorname",      label: "First Name" },
  { key: "personName",         label: "Last Name" },
];

const AUTO_MAP = {
  betreff:            ["Subject", "Betreff", "Title"],
  beschreibung:       ["Description", "Beschreibung", "Notes"],
  datum:              ["Date", "Datum", "Invoice Date"],
  brutto:             ["Gross", "Brutto", "Total", "Amount"],
  netto:              ["Net", "Netto"],
  finalBillingNumber: ["Invoice Nr", "Invoice Number", "Rechnungsnummer"],
  paymentdate:        ["Paid On", "Payment Date", "Zahlungsdatum"],
  bezahlt:            ["Paid", "Bezahlt"],
  status:             ["Status"],
  currency:           ["Currency", "Währung"],
  typ:                ["Type", "Typ"],
  kundeName:          ["Company", "Kunde", "Firma", "Company Name"],
  personVorname:      ["First Name", "Vorname"],
  personName:         ["Last Name", "Nachname", "Name"],
};

const ACTION_CFG = {
  create: { label: "Create",  bg: "#e8f5e9", color: "#2e7d32", border: "#a5d6a7", icon: faCheck },
  skip:   { label: "Skip",    bg: "#fff3e0", color: "#e65100", border: "#ffcc80", icon: faForward },
  ignore: { label: "Ignore",  bg: "#fce4ec", color: "#c62828", border: "#ef9a9a", icon: faXmark },
};

const confidenceBadge = (confidence) => {
  if (!confidence) return null;
  const cfg = {
    exact:   { bg: "#e8f5e9", color: "#2e7d32", border: "#a5d6a7" },
    partial: { bg: "#fff3e0", color: "#e65100", border: "#ffcc80" },
    possible:{ bg: "#fff8e1", color: "#f57f17", border: "#ffe082" },
  }[confidence] || { bg: "#f5f5f5", color: "#757575", border: "#e0e0e0" };
  return (
    <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: "10px", fontSize: "0.72rem", fontWeight: 700, background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}` }}>
      {confidence}
    </span>
  );
};

const parseHeaders = (text, sep) =>
  text.split(/\r?\n/)[0].split(sep).map((h) => h.trim().replace(/^"|"$/g, "")).filter(Boolean);

const buildConfirmRow = (row, action) => {
  if (action === "skip" || action === "ignore") return { action: "skip", betreff: row.betreff };
  return {
    action: "create",
    betreff: row.betreff,
    beschreibung: row.beschreibung,
    datum: row.datum,
    brutto: row.brutto,
    netto: row.netto,
    currency: row.currency,
    typ: row.typ,
    finalBillingNumber: row.finalBillingNumber,
    paymentdate: row.paymentdate,
    bezahlt: row.bezahlt,
    existingKundeId: row.kundeMatch?.kundeId ?? null,
    existingPersonId: row.personMatch?.personId ?? null,
  };
};

const FinanceCsvImport = ({ direction, onImported }) => {
  const [pendingFile, setPendingFile]     = useState(null);
  const [rawText, setRawText]             = useState("");
  const [separator, setSeparator]         = useState(",");
  const [csvHeaders, setCsvHeaders]       = useState([]);
  const [showMapping, setShowMapping]     = useState(false);
  const [columnMapping, setColumnMapping] = useState({});
  const [importRows, setImportRows]       = useState(null);
  const [showImport, setShowImport]       = useState(false);
  const [loading, setLoading]             = useState(false);
  const [confirming, setConfirming]       = useState(false);
  const [rowActions, setRowActions]       = useState({});
  const [selectedRows, setSelectedRows]   = useState(new Set());
  const [filter, setFilter]               = useState("all");

  const applyHeaders = (text, sep) => {
    const headers = parseHeaders(text, sep);
    setCsvHeaders(headers);
    const autoMapping = {};
    MAPPING_FIELDS.forEach(({ key }) => {
      const candidates = AUTO_MAP[key] || [];
      const match = candidates.find((c) => headers.some((h) => h.toLowerCase() === c.toLowerCase()));
      autoMapping[key] = match ? headers.find((h) => h.toLowerCase() === match.toLowerCase()) : "";
    });
    setColumnMapping(autoMapping);
  };

  const handleSeparatorChange = (sep) => {
    setSeparator(sep);
    if (rawText) applyHeaders(rawText, sep);
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = "";
    const text = await file.text();
    setRawText(text);
    setPendingFile(file);
    applyHeaders(text, separator);
    setShowMapping(true);
  };

  const runPreview = (file, mapping) => {
    const reader = new FileReader();
    reader.onloadend = async () => {
      setLoading(true);
      setImportRows(null);
      setRowActions({});
      setSelectedRows(new Set());
      setFilter("all");
      const cleanMapping = Object.fromEntries(Object.entries(mapping).filter(([, v]) => v));
      try {
        const res = await crudApi.createNew(
          `/finance/${direction}/import/preview`,
          { file_string: reader.result, file_name: file.name, mapping: cleanMapping, separator },
          null,
          true
        );
        setImportRows(res?.data ?? []);
        setShowImport(true);
      } catch (err) {
        console.error("[Finance CSV] preview failed:", err);
      }
      setLoading(false);
    };
    reader.readAsDataURL(file);
  };

  const setAction = (rowIndex, action) => {
    setRowActions((prev) => ({ ...prev, [rowIndex]: action }));
  };

  const handleActionBtn = (row, action) => {
    const idx = row.rowIndex;
    if (selectedRows.has(idx)) {
      // Bulk: apply to all selected
      setRowActions((prev) => {
        const next = { ...prev };
        selectedRows.forEach((i) => { next[i] = action; });
        return next;
      });
      if (action === "ignore") {
        // Uncheck all rows that just got ignored
        const affected = new Set(selectedRows);
        setSelectedRows((prev) => {
          const next = new Set(prev);
          affected.forEach((i) => next.delete(i));
          return next;
        });
      }
    } else {
      setAction(idx, action);
      if (action === "ignore") {
        setSelectedRows((prev) => { const next = new Set(prev); next.delete(idx); return next; });
      } else {
        setSelectedRows((prev) => new Set(prev).add(idx));
      }
    }
  };

  const toggleRow = (idx) => {
    if (rowActions[idx] === "ignore") return; // ignored rows can't be selected
    setSelectedRows((prev) => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };

  const toggleAll = () => {
    const selectable = filteredRows
      .filter((r) => rowActions[r.rowIndex] !== "ignore")
      .map((r) => r.rowIndex);
    const ignoredInView = filteredRows
      .filter((r) => rowActions[r.rowIndex] === "ignore")
      .map((r) => r.rowIndex);
    const allSelected = selectable.length > 0 && selectable.every((i) => selectedRows.has(i));
    setSelectedRows((prev) => {
      const next = new Set(prev);
      ignoredInView.forEach((i) => next.delete(i)); // always evict ignored rows
      if (allSelected) selectable.forEach((i) => next.delete(i));
      else selectable.forEach((i) => next.add(i));
      return next;
    });
  };

  const applyToSelected = (action) => {
    setRowActions((prev) => {
      const next = { ...prev };
      selectedRows.forEach((i) => { next[i] = action; });
      return next;
    });
    if (action === "ignore") {
      setSelectedRows(new Set());
    }
  };

  const handleConfirm = async () => {
    const rows = (importRows ?? []).map((row) => {
      const action = rowActions[row.rowIndex] ?? "skip";
      return buildConfirmRow(row, action);
    });
    setConfirming(true);
    try {
      await crudApi.createNew(`/finance/${direction}/import/confirm`, { rows }, null, true);
      setShowImport(false);
      setImportRows(null);
      onImported?.();
    } catch (err) {
      console.error("[Finance CSV] confirm failed:", err);
    }
    setConfirming(false);
  };

  const filteredRows = (importRows ?? []).filter((row) => {
    if (filter === "all") return true;
    if (filter === "unset") return !rowActions[row.rowIndex];
    return rowActions[row.rowIndex] === filter;
  });

  const createCount = (importRows ?? []).filter((r) => rowActions[r.rowIndex] === "create").length;
  const skipCount   = (importRows ?? []).filter((r) => rowActions[r.rowIndex] === "skip").length;
  const ignoreCount = (importRows ?? []).filter((r) => rowActions[r.rowIndex] === "ignore").length;
  const unsetCount  = (importRows ?? []).filter((r) => !rowActions[r.rowIndex]).length;

  const mappedCount = Object.values(columnMapping).filter(Boolean).length;

  const cell = { padding: "10px 12px", fontSize: "0.83rem", color: "#424242", verticalAlign: "middle" };

  return (
    <>
      <style>{btnHoverStyle}</style>

      {/* Mapping modal */}
      <Modal show={showMapping} onHide={() => setShowMapping(false)} centered size="lg" contentClassName="modal-pipeline-content">
        <div style={{ background: "linear-gradient(135deg, #1a73e8 0%, #0d47a1 100%)", padding: "18px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", borderRadius: "14px 14px 0 0" }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: "1rem", color: "#fff" }}>Map CSV Columns</div>
            <div style={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.7)", marginTop: "2px" }}>{pendingFile?.name} · {csvHeaders.length} columns detected</div>
          </div>
          <button onClick={() => setShowMapping(false)} style={{ background: "rgba(255,255,255,0.15)", border: "none", borderRadius: "8px", width: "30px", height: "30px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#fff", fontSize: "1rem" }}>✕</button>
        </div>
        <div style={{ background: "#f8f9fb", padding: "0 24px 4px", maxHeight: "60vh", overflowY: "auto" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "14px 0 12px", borderBottom: "2px solid #e8edf5" }}>
            <span style={{ fontSize: "0.78rem", fontWeight: 700, color: "#616161" }}>Separator:</span>
            {[{ value: ",", label: "Comma  ," }, { value: ";", label: "Semicolon  ;" }].map(({ value, label }) => (
              <button key={value} onClick={() => handleSeparatorChange(value)} style={{ padding: "5px 14px", borderRadius: "8px", border: `1px solid ${separator === value ? "#1a73e8" : "#e0e0e0"}`, background: separator === value ? "#e8f0fe" : "#fff", color: separator === value ? "#1a73e8" : "#616161", fontWeight: separator === value ? 700 : 500, fontSize: "0.83rem", cursor: "pointer" }}>
                {label}
              </button>
            ))}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 40px 1fr", padding: "12px 0 8px", borderBottom: "2px solid #e8edf5" }}>
            <div style={{ fontSize: "0.72rem", fontWeight: 800, color: "#1a73e8", textTransform: "uppercase", letterSpacing: "0.06em" }}>Column in your CSV</div>
            <div />
            <div style={{ fontSize: "0.72rem", fontWeight: 800, color: "#424242", textTransform: "uppercase", letterSpacing: "0.06em" }}>Imported as</div>
          </div>
          {MAPPING_FIELDS.map(({ key, label }) => {
            const mapped = !!columnMapping[key];
            return (
              <div key={key} style={{ display: "grid", gridTemplateColumns: "1fr 40px 1fr", alignItems: "center", padding: "8px 0", borderBottom: "1px solid #f0f0f0" }}>
                <select value={columnMapping[key] || ""} onChange={(e) => setColumnMapping((prev) => ({ ...prev, [key]: e.target.value }))} style={{ padding: "7px 10px", border: `1px solid ${mapped ? "#93b4f5" : "#e0e0e0"}`, borderRadius: "8px", fontSize: "0.88rem", background: mapped ? "#f0f4ff" : "#fff", color: mapped ? "#1a73e8" : "#9e9e9e", outline: "none", cursor: "pointer", fontWeight: mapped ? 600 : 400 }}>
                  <option value="">— not mapped —</option>
                  {csvHeaders.map((h) => <option key={h} value={h}>{h}</option>)}
                </select>
                <div style={{ textAlign: "center", fontSize: "1rem", color: mapped ? "#1a73e8" : "#d0d0d0", fontWeight: 700 }}>→</div>
                <div style={{ padding: "7px 12px", borderRadius: "8px", background: mapped ? "#e8f0fe" : "#f5f5f5", border: `1px solid ${mapped ? "#93b4f5" : "#e8e8e8"}` }}>
                  <span style={{ fontSize: "0.88rem", fontWeight: 700, color: mapped ? "#212121" : "#9e9e9e" }}>{label}</span>
                </div>
              </div>
            );
          })}
          <div style={{ padding: "12px 0 16px", fontSize: "0.78rem", color: "#9e9e9e" }}>
            Unmapped fields will be skipped · auto-matched {mappedCount} of {MAPPING_FIELDS.length} fields
          </div>
        </div>
        <div style={{ background: "#fff", borderTop: "1px solid #f0f0f0", padding: "14px 24px", display: "flex", justifyContent: "flex-end", gap: "10px", borderRadius: "0 0 14px 14px" }}>
          <button onClick={() => setShowMapping(false)} style={{ padding: "8px 18px", background: "#f5f5f5", color: "#424242", border: "none", borderRadius: "8px", fontWeight: 600, fontSize: "0.88rem", cursor: "pointer" }}>Cancel</button>
          <button onClick={() => { setShowMapping(false); runPreview(pendingFile, columnMapping); }} disabled={loading} style={{ padding: "8px 22px", background: "#1a73e8", color: "#fff", border: "none", borderRadius: "8px", fontWeight: 700, fontSize: "0.88rem", cursor: "pointer" }}>
            Preview Import
          </button>
        </div>
      </Modal>

      {/* Full-page import view */}
      {showImport && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1050, background: "#f4f6fa", display: "flex", flexDirection: "column" }}>
          {/* Header */}
          <div style={{ background: "linear-gradient(135deg, #1a73e8 0%, #0d47a1 100%)", padding: "16px 28px", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0, boxShadow: "0 2px 12px rgba(0,0,0,0.15)" }}>
            <div>
              <div style={{ fontWeight: 800, fontSize: "1.1rem", color: "#fff" }}>Review Import</div>
              <div style={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.75)", marginTop: "2px" }}>
                {pendingFile?.name} · {importRows?.length ?? 0} rows · assign an action to each row, then confirm
              </div>
            </div>
            <button onClick={() => setShowImport(false)} style={{ background: "rgba(255,255,255,0.15)", border: "none", borderRadius: "8px", width: "34px", height: "34px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#fff", fontSize: "1.1rem" }}>✕</button>
          </div>

          {/* Toolbar */}
          <div style={{ background: "#fff", borderBottom: "1px solid #e8edf5", padding: "12px 28px", display: "flex", alignItems: "center", gap: "12px", flexShrink: 0, flexWrap: "wrap" }}>
            {/* Filter chips */}
            <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
              {[
                { key: "all",    label: `All (${importRows?.length ?? 0})` },
                { key: "unset",  label: `Unset (${unsetCount})`,   color: "#9e9e9e" },
                { key: "create", label: `Create (${createCount})`, color: ACTION_CFG.create.color },
                { key: "skip",   label: `Skip (${skipCount})`,     color: ACTION_CFG.skip.color },
                { key: "ignore", label: `Ignore (${ignoreCount})`, color: ACTION_CFG.ignore.color },
              ].map(({ key, label, color }) => (
                <button key={key} onClick={() => setFilter(key)} style={{ padding: "5px 13px", borderRadius: "20px", border: `1px solid ${filter === key ? (color || "#1a73e8") : "#e0e0e0"}`, background: filter === key ? (color ? color + "18" : "#e8f0fe") : "#f5f5f5", color: filter === key ? (color || "#1a73e8") : "#616161", fontWeight: filter === key ? 700 : 500, fontSize: "0.8rem", cursor: "pointer" }}>
                  {label}
                </button>
              ))}
            </div>

            {/* Bulk action bar */}
            {selectedRows.size > 0 && (
              <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "8px", background: "#e8f0fe", borderRadius: "10px", padding: "6px 14px" }}>
                <span style={{ fontSize: "0.8rem", fontWeight: 700, color: "#1a73e8" }}>
                  <FontAwesomeIcon icon={faCheckSquare} style={{ marginRight: "5px" }} />
                  {selectedRows.size} selected
                </span>
                {Object.entries(ACTION_CFG).map(([action, cfg]) => (
                  <button key={action} onClick={() => applyToSelected(action)} style={{ padding: "4px 12px", borderRadius: "8px", border: `1px solid ${cfg.border}`, background: cfg.bg, color: cfg.color, fontWeight: 700, fontSize: "0.78rem", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "5px" }}>
                    <FontAwesomeIcon icon={cfg.icon} />
                    {cfg.label} all
                  </button>
                ))}
              </div>
            )}

            {/* Upload different file */}
            <label style={{ marginLeft: selectedRows.size > 0 ? undefined : "auto", padding: "6px 14px", borderRadius: "8px", border: "1px solid #e0e0e0", background: "#f5f5f5", color: "#616161", fontWeight: 600, fontSize: "0.8rem", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "6px" }}>
              <FontAwesomeIcon icon={faFileArrowUp} /> Different file
              <input type="file" accept=".csv,text/csv" onChange={(e) => { setShowImport(false); handleFileSelect(e); }} style={{ display: "none" }} />
            </label>
          </div>

          {/* Table */}
          <div style={{ flex: 1, overflowY: "auto", padding: "20px 28px" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", background: "#fff", borderRadius: "12px", overflow: "hidden", boxShadow: "0 1px 8px rgba(0,0,0,0.07)", fontSize: "0.83rem" }}>
              <thead>
                <tr style={{ background: "#f8f9fb", borderBottom: "2px solid #f0f0f0" }}>
                  <th style={{ padding: "10px 12px", width: "36px" }}>
                    {(() => {
                      const selectable = filteredRows.filter((r) => rowActions[r.rowIndex] !== "ignore");
                      return <input type="checkbox" checked={selectable.length > 0 && selectable.every((r) => selectedRows.has(r.rowIndex))} onChange={toggleAll} style={{ cursor: "pointer" }} />;
                    })()}
                  </th>
                  {["Invoice Nr", "Subject", "Date", "Gross", "Net", "Company / Person", "Duplicate", "Customer", "Person", "Action"].map((h) => (
                    <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontSize: "0.7rem", fontWeight: 800, color: "#9e9e9e", textTransform: "uppercase", letterSpacing: "0.07em", whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredRows.length === 0 ? (
                  <tr><td colSpan={11} style={{ textAlign: "center", padding: "48px", color: "#bdbdbd" }}>No rows match this filter.</td></tr>
                ) : filteredRows.map((row) => {
                  const action = rowActions[row.rowIndex];
                  const cfg = action ? ACTION_CFG[action] : null;
                  const isSelected = selectedRows.has(row.rowIndex);
                  return (
                    <tr key={row.rowIndex} style={{ borderBottom: "1px solid #f5f5f5", background: isSelected ? "#f0f4ff" : (cfg ? cfg.bg + "55" : "#fff"), transition: "background 0.1s" }}>
                      <td style={{ padding: "10px 12px" }}>
                        <input type="checkbox" checked={isSelected && action !== "ignore"} disabled={action === "ignore"} onChange={() => toggleRow(row.rowIndex)} style={{ cursor: action === "ignore" ? "not-allowed" : "pointer", opacity: action === "ignore" ? 0.35 : 1 }} />
                      </td>
                      <td style={{ ...cell, fontWeight: 700, color: "#212121", whiteSpace: "nowrap" }}>{row.finalBillingNumber || "—"}</td>
                      <td style={{ ...cell, maxWidth: "180px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.betreff || "—"}</td>
                      <td style={{ ...cell, whiteSpace: "nowrap" }}>{row.datum || "—"}</td>
                      <td style={{ ...cell, textAlign: "right", fontWeight: 700, color: row.eingang ? "#c62828" : "#2e7d32", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
                        {row.brutto != null ? `${Number(row.brutto).toLocaleString("de-DE", { minimumFractionDigits: 2 })} ${row.currency || "€"}` : "—"}
                      </td>
                      <td style={{ ...cell, textAlign: "right", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
                        {row.netto != null ? Number(row.netto).toLocaleString("de-DE", { minimumFractionDigits: 2 }) : "—"}
                      </td>
                      <td style={{ ...cell, maxWidth: "160px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {[row.kundeName, [row.personVorname, row.personName].filter(Boolean).join(" ")].filter(Boolean).join(" · ") || "—"}
                      </td>
                      <td style={{ ...cell }}>
                        {row.duplicateMatch ? (
                          <div>{confidenceBadge(row.duplicateMatch.confidence)}<div style={{ fontSize: "0.72rem", color: "#9e9e9e", marginTop: "3px", maxWidth: "120px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.duplicateMatch.finalBillingNumber}</div></div>
                        ) : <span style={{ color: "#d0d0d0" }}>—</span>}
                      </td>
                      <td style={{ ...cell }}>
                        {row.kundeMatch ? (
                          <div>{confidenceBadge(row.kundeMatch.confidence)}<div style={{ fontSize: "0.72rem", color: "#9e9e9e", marginTop: "3px" }}>{row.kundeMatch.firmenname}</div></div>
                        ) : <span style={{ color: "#d0d0d0" }}>—</span>}
                      </td>
                      <td style={{ ...cell }}>
                        {row.personMatch ? (
                          <div>{confidenceBadge(row.personMatch.confidence)}<div style={{ fontSize: "0.72rem", color: "#9e9e9e", marginTop: "3px" }}>{[row.personMatch.vorname, row.personMatch.name].filter(Boolean).join(" ")}</div></div>
                        ) : <span style={{ color: "#d0d0d0" }}>—</span>}
                      </td>
                      <td style={{ ...cell, whiteSpace: "nowrap" }}>
                        <div style={{ display: "flex", gap: "5px" }}>
                          {Object.entries(ACTION_CFG).map(([act, c]) => (
                            <button key={act} onClick={() => handleActionBtn(row, act)} style={{ padding: "4px 10px", borderRadius: "7px", border: `1px solid ${action === act ? c.border : "#e0e0e0"}`, background: action === act ? c.bg : "#f8f8f8", color: action === act ? c.color : "#9e9e9e", fontWeight: action === act ? 700 : 500, fontSize: "0.75rem", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "4px", transition: "all 0.1s" }}>
                              <FontAwesomeIcon icon={c.icon} />
                              {c.label}
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Footer */}
          <div style={{ background: "#fff", borderTop: "1px solid #e8edf5", padding: "14px 28px", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0, boxShadow: "0 -2px 8px rgba(0,0,0,0.06)" }}>
            <div style={{ display: "flex", gap: "16px", fontSize: "0.82rem" }}>
              {createCount > 0 && <span style={{ color: ACTION_CFG.create.color, fontWeight: 700 }}><FontAwesomeIcon icon={faCheck} style={{ marginRight: "4px" }} />{createCount} to create</span>}
              {skipCount > 0   && <span style={{ color: ACTION_CFG.skip.color,   fontWeight: 700 }}><FontAwesomeIcon icon={faForward} style={{ marginRight: "4px" }} />{skipCount} to skip</span>}
              {ignoreCount > 0 && <span style={{ color: ACTION_CFG.ignore.color, fontWeight: 700 }}><FontAwesomeIcon icon={faXmark} style={{ marginRight: "4px" }} />{ignoreCount} to ignore</span>}
              {unsetCount > 0  && <span style={{ color: "#9e9e9e" }}>{unsetCount} unset (will be skipped)</span>}
            </div>
            <div style={{ display: "flex", gap: "10px" }}>
              <button onClick={() => setShowImport(false)} style={{ padding: "9px 20px", background: "#f5f5f5", color: "#424242", border: "none", borderRadius: "8px", fontWeight: 600, fontSize: "0.88rem", cursor: "pointer" }}>Cancel</button>
              <button
                onClick={handleConfirm}
                disabled={confirming || createCount === 0}
                style={{ padding: "9px 24px", background: createCount > 0 ? "#1a73e8" : "#b0bec5", color: "#fff", border: "none", borderRadius: "8px", fontWeight: 700, fontSize: "0.88rem", cursor: createCount > 0 ? "pointer" : "default", display: "inline-flex", alignItems: "center", gap: "8px" }}
              >
                {confirming ? <><FontAwesomeIcon icon={faSpinner} spin /> Importing…</> : `Import ${createCount} bill${createCount !== 1 ? "s" : ""}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Trigger button */}
      <label className="btn-modern" style={{ ...btn.base, ...btn.secondary, cursor: loading ? "default" : "pointer", display: "inline-flex", alignItems: "center", gap: "6px", opacity: loading ? 0.7 : 1 }}>
        {loading
          ? <><FontAwesomeIcon icon={faSpinner} spin /> Analyzing…</>
          : <><FontAwesomeIcon icon={faFileArrowUp} /> Import CSV</>}
        <input type="file" accept=".csv,text/csv" onChange={handleFileSelect} disabled={loading} style={{ display: "none" }} />
      </label>
    </>
  );
};

export default FinanceCsvImport;
